// Phase B1 — citation submission tracker. Cross-portfolio view: pick a
// site, see its citation submissions across all platforms, expand to
// view the pre-filled packet, mark submitted/live as the operator
// works through the queue.
//
// Per LocalSEOPlaybook §5.2, the goal is ~30 citations in the first
// 30 days of a site's life, weighted toward tier-1 (Yelp, BBB, Apple
// Business, Foursquare, Bing Places, Nextdoor).

import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type Citation, citationsApi, type OperatorListing, operatorListingsApi } from "../lib/api";

type SortKey = "leverage" | "domain" | "queued" | "submitted" | "live" | "pct";

export function CitationsPage() {
  const portfolioQ = useQuery({
    queryKey: ["citations", "portfolio"],
    queryFn: citationsApi.portfolio,
    refetchInterval: 60_000,
  });
  const nextQ = useQuery({
    queryKey: ["citations", "next-actions"],
    queryFn: () => citationsApi.nextActions(10),
    refetchInterval: 60_000,
  });
  const [expandedSiteId, setExpandedSiteId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("leverage");
  const [niche, setNiche] = useState<string>("");
  const [hideComplete, setHideComplete] = useState<boolean>(false);

  const summary = portfolioQ.data?.summary;
  const sitesRaw = portfolioQ.data?.sites ?? [];
  const niches = Array.from(new Set(sitesRaw.map((s) => s.niche).filter((x): x is string => !!x))).sort();
  let sites = sitesRaw.filter((s) => !niche || s.niche === niche);
  if (hideComplete) sites = sites.filter((s) => s.queued > 0);
  sites = [...sites].sort((a, b) => {
    switch (sortKey) {
      case "domain": return a.domain.localeCompare(b.domain);
      case "queued": return b.queued - a.queued;
      case "submitted": return b.submitted - a.submitted;
      case "live": return b.live - a.live;
      case "pct": return (b.total ? (b.submitted + b.live) / b.total : 0) - (a.total ? (a.submitted + a.live) / a.total : 0);
      default: return b.top30Kws - a.top30Kws;
    }
  });
  const totalPct = summary && summary.total > 0 ? Math.round(100 * (summary.submitted + summary.live) / summary.total) : 0;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold">Citations</h1>
          <p className="text-ink-600 text-sm mt-1">
            Portfolio-wide citation tracker. Goal per LocalSEOPlaybook §5.2: ~30 tier-1 citations per site over the first 30 days.
          </p>
        </div>
      </div>

      <OperatorListingsCard />

      {/* ─── Summary strip ───────────────────────────────────────────── */}
      {portfolioQ.isLoading && <Loading />}
      {portfolioQ.isError && <ErrorBox msg={String((portfolioQ.error as Error).message)} />}
      {summary && (
        <div className="bg-white rounded-xl border border-ink-100 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-500 font-medium">Total</div>
              <div className="text-2xl font-mono font-semibold">{summary.total}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-500 font-medium">Queued</div>
              <div className="text-2xl font-mono font-semibold text-ink-700">{summary.queued}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-500 font-medium">Submitted</div>
              <div className="text-2xl font-mono font-semibold text-amber-700">{summary.submitted}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-500 font-medium">Live</div>
              <div className="text-2xl font-mono font-semibold text-green-700">{summary.live}</div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs uppercase tracking-wider text-ink-500 font-medium">Complete</div>
              <div className="flex items-center gap-3 mt-1">
                <div className="flex-1 h-3 bg-ink-100 rounded overflow-hidden">
                  <div className="h-full bg-green-500" style={{ width: `${totalPct}%` }} />
                </div>
                <div className="text-lg font-mono font-semibold">{totalPct}%</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Top 10 next-actions ─────────────────────────────────────── */}
      <section className="bg-white rounded-xl border border-ink-100 mb-6 overflow-hidden">
        <div className="px-4 py-3 border-b border-ink-100">
          <h2 className="text-lg font-semibold">Top 10 to file next <span className="text-ink-500 text-sm font-normal">— ranked by leverage (site rank-strength × platform tier)</span></h2>
        </div>
        {nextQ.isLoading && <div className="p-4 text-sm text-ink-500">Loading…</div>}
        {nextQ.isError && <div className="p-4 text-sm text-rose-600">Failed to load</div>}
        {nextQ.data && nextQ.data.actions.length === 0 && (
          <div className="p-4 text-sm text-ink-500">Nothing queued. Either every citation is submitted/live or no packets exist yet.</div>
        )}
        {nextQ.data && nextQ.data.actions.length > 0 && (
          <ol className="divide-y divide-ink-100">
            {nextQ.data.actions.map((a, i) => (
              <li key={a.citationId} className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-ink-50">
                <span className="text-ink-400 font-mono w-6 text-right">{i + 1}</span>
                <span className={`inline-flex items-center justify-center w-8 h-5 text-xs font-mono rounded ${a.tier === 1 ? "bg-blue-100 text-blue-800" : a.tier === 2 ? "bg-purple-100 text-purple-800" : "bg-ink-100 text-ink-700"}`}>T{a.tier}</span>
                <span className="font-medium text-ink-700 capitalize">{a.platform.replace(/_/g, " ")}</span>
                <span className="text-ink-400">→</span>
                <button
                  onClick={() => { setExpandedSiteId(a.siteId); document.getElementById(`site-${a.siteId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
                  className="text-blue-600 hover:underline truncate"
                >{a.domain}</button>
                <span className="text-ink-400 text-xs flex-1">{a.niche ?? ""}</span>
                <span className="text-ink-500 text-xs whitespace-nowrap">leverage {a.leverage}</span>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* ─── All sites table ─────────────────────────────────────────── */}
      <section className="bg-white rounded-xl border border-ink-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-ink-100 flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold flex-1 min-w-[150px]">All sites</h2>
          <select
            value={niche}
            onChange={(e) => setNiche(e.target.value)}
            className="px-2 py-1 border border-ink-200 rounded text-sm bg-white"
          >
            <option value="">All niches</option>
            {niches.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <label className="text-sm text-ink-600 flex items-center gap-1">
            <input type="checkbox" checked={hideComplete} onChange={(e) => setHideComplete(e.target.checked)} />
            Hide complete
          </label>
          <span className="text-xs text-ink-500">{sites.length} sites</span>
        </div>
        {sites.length === 0 && !portfolioQ.isLoading && (
          <div className="p-4 text-sm text-ink-500">No sites match the current filter.</div>
        )}
        {sites.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase text-ink-600">
                <tr>
                  <th className="text-left px-4 py-2 font-medium cursor-pointer" onClick={() => setSortKey("domain")}>Site</th>
                  <th className="text-left px-4 py-2 font-medium">Niche</th>
                  <th className="text-right px-4 py-2 font-medium cursor-pointer" onClick={() => setSortKey("queued")}>Q</th>
                  <th className="text-right px-4 py-2 font-medium cursor-pointer" onClick={() => setSortKey("submitted")}>Sub</th>
                  <th className="text-right px-4 py-2 font-medium cursor-pointer" onClick={() => setSortKey("live")}>Live</th>
                  <th className="text-right px-4 py-2 font-medium">Tier-1</th>
                  <th className="text-left px-4 py-2 font-medium cursor-pointer" onClick={() => setSortKey("pct")}>Complete</th>
                  <th className="text-right px-4 py-2 font-medium cursor-pointer" onClick={() => setSortKey("leverage")}>Top-30 kws</th>
                </tr>
              </thead>
              <tbody>
                {sites.map((s) => {
                  const isExpanded = expandedSiteId === s.siteId;
                  const pct = s.total > 0 ? Math.round(100 * (s.submitted + s.live) / s.total) : 0;
                  return (
                    <Fragment key={s.siteId}>
                      <tr
                        id={`site-${s.siteId}`}
                        className={`border-t border-ink-100 cursor-pointer ${isExpanded ? "bg-blue-50" : "hover:bg-ink-50"}`}
                        onClick={() => setExpandedSiteId(isExpanded ? null : s.siteId)}
                      >
                        <td className="px-4 py-2">
                          <span className="text-ink-400 mr-2">{isExpanded ? "▾" : "▸"}</span>
                          <span className="text-blue-600">{s.domain}</span>
                        </td>
                        <td className="px-4 py-2 text-ink-600 text-xs">{s.niche ?? "—"}</td>
                        <td className="px-4 py-2 text-right font-mono">{s.queued}</td>
                        <td className="px-4 py-2 text-right font-mono text-amber-700">{s.submitted}</td>
                        <td className="px-4 py-2 text-right font-mono text-green-700">{s.live}</td>
                        <td className="px-4 py-2 text-right text-xs text-ink-600">{s.tier1Done}/{s.tier1Total}</td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 bg-ink-100 rounded overflow-hidden">
                              <div className="h-full bg-green-500" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-ink-600 font-mono w-8 text-right">{pct}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right text-xs text-ink-500 font-mono">{s.top30Kws}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-ink-50/50">
                          <td colSpan={8} className="px-4 py-4">
                            <CitationsList siteId={s.siteId} domain={s.domain} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function CitationsList({ siteId, domain }: { siteId: string; domain: string }) {
  const q = useQuery({
    queryKey: ["citations", siteId],
    queryFn: () => citationsApi.forSite(siteId),
    refetchInterval: 60_000,
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox msg={String((q.error as Error).message)} />;
  const items = q.data?.citations ?? [];
  if (items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-ink-100 p-8 text-center">
        <p className="text-ink-700 font-medium">No citations queued for {domain} yet.</p>
        <p className="text-ink-500 text-sm mt-2">
          Run{" "}
          <code className="bg-ink-100 px-1.5 py-0.5 rounded text-xs">
            rankforge citations-packet --domain {domain}
          </code>{" "}
          to generate the platform packet list.
        </p>
      </div>
    );
  }
  const counts = {
    queued: items.filter((c) => c.status === "queued").length,
    submitted: items.filter((c) => c.status === "submitted").length,
    live: items.filter((c) => c.status === "live").length,
    rejected: items.filter((c) => c.status === "rejected").length,
  };
  const byTier: Record<number, Citation[]> = { 1: [], 2: [], 3: [] };
  for (const c of items) (byTier[c.tier] ?? (byTier[c.tier] = [])).push(c);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 text-sm">
        <Stat label="Live" value={counts.live} tone="green" />
        <Stat label="Submitted" value={counts.submitted} tone="amber" />
        <Stat label="Queued" value={counts.queued} tone="grey" />
        <Stat label="Rejected" value={counts.rejected} tone="red" />
      </div>
      {([1, 2, 3] as const).map((tier) => (
        byTier[tier]!.length > 0 && (
          <div key={tier}>
            <h3 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700 mb-2">
              Tier {tier} {tier === 1 ? "— mandatory" : tier === 2 ? "— niche" : "— long tail"}
            </h3>
            <div className="bg-white rounded-xl border border-ink-100 overflow-hidden">
              {byTier[tier]!.map((c) => (
                <CitationRow key={c.id} citation={c} />
              ))}
            </div>
          </div>
        )
      ))}
    </div>
  );
}

function CitationRow({ citation }: { citation: Citation }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [listingUrl, setListingUrl] = useState(citation.listingUrl ?? "");
  const update = useMutation({
    mutationFn: (patch: Parameters<typeof citationsApi.update>[1]) => citationsApi.update(citation.id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["citations"] }),
  });
  const tone: Record<string, string> = {
    queued: "bg-ink-100 text-ink-700",
    submitted: "bg-amber-100 text-amber-800",
    live: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    needs_review: "bg-blue-100 text-blue-800",
  };
  const packet = citation.packet as
    | {
        platform: { name: string; submitUrl: string; notes?: string };
        fields: {
          businessName: string;
          address: { street: string; city: string; state: string; zip: string };
          phone: string;
          displayPhone: string;
          email: string;
          website: string;
          categories: string[];
          description: string;
          serviceAreas: string[];
          photoChecklist: string[];
        };
        verificationUrl: string;
      }
    | null;

  return (
    <div className="border-b border-ink-100 last:border-0">
      <div className="flex items-center gap-3 p-3 hover:bg-ink-50/50">
        <button onClick={() => setExpanded((e) => !e)} className="text-ink-400 hover:text-ink-700 w-4 text-left">
          {expanded ? "▾" : "▸"}
        </button>
        <div className="flex-1 flex items-baseline gap-2">
          <span className="font-medium text-ink-900">{packet?.platform?.name ?? citation.platform}</span>
          {packet?.platform?.submitUrl && (
            <a href={packet.platform.submitUrl} target="_blank" rel="noreferrer" className="text-xs text-accent">submit ↗</a>
          )}
          {citation.listingUrl && (
            <a href={citation.listingUrl} target="_blank" rel="noreferrer" className="text-xs text-green-700">live listing ↗</a>
          )}
        </div>
        <span className={`inline-flex px-2 py-0.5 text-xs rounded-md font-bold ${tone[citation.status]}`}>
          {citation.status}
        </span>
        <div className="flex gap-1 text-xs">
          {citation.status === "queued" && (
            <button onClick={() => update.mutate({ markSubmitted: true })}
                    disabled={update.isPending}
                    className="px-2 py-0.5 bg-amber-600 text-white rounded font-medium disabled:opacity-50">
              Mark submitted
            </button>
          )}
          {citation.status !== "live" && (
            <button onClick={() => update.mutate({ markLive: true, listingUrl })}
                    disabled={update.isPending || !listingUrl}
                    title={!listingUrl ? "Enter listing URL first (expand row)" : ""}
                    className="px-2 py-0.5 bg-green-600 text-white rounded font-medium disabled:opacity-50">
              Mark live
            </button>
          )}
        </div>
      </div>
      {expanded && packet && (
        <div className="bg-ink-50/40 p-4 border-t border-ink-100 space-y-3 text-sm">
          {packet.platform.notes && (
            <p className="text-xs text-ink-500 italic">{packet.platform.notes}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <Kv k="Business name" v={packet.fields.businessName} />
            <Kv k="Phone (display)" v={packet.fields.displayPhone} />
            <Kv k="Email" v={packet.fields.email} />
            <Kv k="Website" v={packet.fields.website} />
            <Kv k="Address" v={`${packet.fields.address.street}, ${packet.fields.address.city}, ${packet.fields.address.state} ${packet.fields.address.zip}`} />
            <Kv k="Service area" v={packet.fields.serviceAreas.join(", ")} />
            <Kv k="Categories" v={packet.fields.categories.join(", ")} />
          </div>
          <div className="text-xs">
            <div className="text-ink-500 uppercase tracking-wider mb-1">Description</div>
            <p className="text-ink-700 leading-relaxed bg-white border border-ink-200 rounded p-2">{packet.fields.description}</p>
          </div>
          <div className="text-xs">
            <div className="text-ink-500 uppercase tracking-wider mb-1">Photo checklist</div>
            <ul className="list-disc pl-5 text-ink-700">
              {packet.fields.photoChecklist.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </div>
          <div className="flex gap-2 items-end pt-2 border-t border-ink-200">
            <label className="flex flex-col gap-0.5 flex-1">
              <span className="text-xs uppercase tracking-wider text-ink-500">Live listing URL</span>
              <input value={listingUrl} onChange={(e) => setListingUrl(e.target.value)}
                     placeholder="https://yelp.com/biz/…"
                     className="px-2 py-1 border border-ink-200 rounded text-xs"/>
            </label>
            <button onClick={() => update.mutate({ listingUrl })}
                    disabled={update.isPending}
                    className="px-3 py-1 bg-ink-900 text-white rounded text-xs font-medium disabled:opacity-50">
              Save URL
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Kv({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div className="text-ink-500 uppercase tracking-wider mb-0.5">{k}</div>
      <div className="text-ink-900 font-mono text-xs select-all">{v}</div>
    </div>
  );
}
function Stat({ label, value, tone }: { label: string; value: number; tone: "green" | "amber" | "grey" | "red" }) {
  const cls = tone === "green" ? "bg-green-100 text-green-800"
            : tone === "amber" ? "bg-amber-100 text-amber-800"
            : tone === "red" ? "bg-red-100 text-red-800"
            : "bg-ink-100 text-ink-700";
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-medium ${cls}`}>
      <span className="font-bold">{value}</span>
      <span>{label}</span>
    </span>
  );
}

function Loading() {
  return <div className="bg-white rounded-xl border border-ink-100 p-8 text-center text-ink-500">Loading…</div>;
}
function ErrorBox({ msg }: { msg: string }) {
  return <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">Failed: {msg}</div>;
}

// Operator-entity listings (operator LLC's own GBP, LinkedIn, BBB).
// Distinct from per-site citations because they're about the lead-gen
// LLC entity itself, not any individual site. Per Override §6.8 the
// operator MUST have its own GBP at the LLC's verifiable address —
// surfacing that here so it doesn't get lost.
function OperatorListingsCard() {
  const q = useQuery({
    queryKey: ["operator-listings"],
    queryFn: operatorListingsApi.list,
  });
  if (q.isLoading) return null;
  if (q.error || !q.data) return null;
  if (q.data.listings.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 mb-4">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">
          Operator entity (lead-gen LLC)
        </h2>
        <span className="text-xs text-ink-500">
          Distinct from per-site citations — these track listings for the operator LLC itself.
        </span>
      </div>
      <div className="space-y-2">
        {q.data.listings.map((l) => (
          <OperatorListingRow key={l.id} listing={l} />
        ))}
      </div>
    </div>
  );
}

function OperatorListingRow({ listing }: { listing: OperatorListing }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (patch: Parameters<typeof operatorListingsApi.update>[1]) =>
      operatorListingsApi.update(listing.id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["operator-listings"] }),
  });
  const [listingUrl, setListingUrl] = useState(listing.listingUrl ?? "");
  const statusCls = listing.status === "live"
    ? "bg-green-100 text-green-800"
    : listing.status === "submitted"
      ? "bg-amber-100 text-amber-800"
      : "bg-ink-100 text-ink-600";
  return (
    <div className="border border-ink-100 rounded-lg p-3 flex flex-wrap items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="font-medium text-ink-900">{listing.name}</span>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusCls}`}>{listing.status}</span>
          {listing.submitUrl && (
            <a href={listing.submitUrl} target="_blank" rel="noopener noreferrer"
               className="text-xs text-accent-600 hover:underline">submit ↗</a>
          )}
        </div>
        {listing.notes && (
          <p className="text-xs text-ink-600 mb-2 whitespace-pre-wrap">{listing.notes}</p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="url"
            placeholder="Live listing URL (after verified)"
            value={listingUrl}
            onChange={(e) => setListingUrl(e.target.value)}
            onBlur={() => {
              if (listingUrl !== (listing.listingUrl ?? "")) m.mutate({ listingUrl });
            }}
            className="flex-1 min-w-[200px] px-2 py-1 text-xs border border-ink-200 rounded"
          />
          {listing.status === "queued" && (
            <button
              onClick={() => m.mutate({ markSubmitted: true })}
              disabled={m.isPending}
              className="text-xs px-2 py-1 bg-amber-50 border border-amber-200 text-amber-800 rounded hover:bg-amber-100"
            >Mark submitted</button>
          )}
          {listing.status !== "live" && (
            <button
              onClick={() => m.mutate({ markLive: true })}
              disabled={m.isPending}
              className="text-xs px-2 py-1 bg-green-50 border border-green-200 text-green-800 rounded hover:bg-green-100"
            >Mark live</button>
          )}
        </div>
      </div>
    </div>
  );
}
